<template>
  <wizard
    first-step="personalDetails"
    class="wizard-childcare-allowance"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="sendRequest"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
      :hide-button="isChecked"
    >
      <wizard-step-intro>
        <h2>{{ i18n.form.intro.title }}</h2>
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
        <kf-rich-text v-html="i18n.form.intro.text" />
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 gap-6">
        <div
          v-if="inDateRange"
          class="col-span-12"
        >
          <FormulateInput
            name="bonusCode"
            type="text"
            label="bonus code"
            validation="nohtml"
            class="custom-form-element"
            aria-hidden="true"
          />
          <FormulateInput
            name="website"
            type="text"
            label="website"
            validation="nohtml"
            class="custom-form-element"
            aria-hidden="true"
          />
          <FormulateInput
            v-model="isChecked"
            name="checkbox"
            :options="[
              {
                value: 'yes',
                label: i18n.form.checkbox.true,
              },
              {
                value: 'no',
                label: i18n.form.checkbox.false,
              },
            ]"
            type="radio"
            :label="i18n.form.checkbox.label"
            validation="required"
            :validation-messages="i18n.form.validations.checkbox"
          />
        </div>
      </fieldset>

      <fieldset
        v-if="(inDateRange && isChecked === 'yes') || !inDateRange"
        class="grid grid-cols-12 gap-6"
      >
        <div class="col-span-12">
          <legend class="mb-4">
            <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
            <kf-rich-text v-html="i18n.form.labels.childcareFormIntro" />
          </legend>
          <FormulateInput
            :label="i18n.form.labels.name"
            name="name"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.name"
          />

          <FormulateInput
            name="email"
            type="email"
            :label="i18n.form.labels.email"
            validation="bail|required:trim|email"
            :validation-messages="i18n.form.validations.email"
          />

          <FormulateInput
            name="phoneNumber"
            type="text"
            :label="i18n.form.labels.phoneNumber"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.phoneNumber"
          />

          <FormulateInput
            type="text"
            name="debtorNumber"
            :label="i18n.form.labels.debtorNumber"
            :help="i18n.form.help.debtorNumber"
            validation="bail|required:trim|max:10,length|nohtml|matches:/^[0-9\-]+$/"
            validation-name="Klantnummer"
            :validation-messages="i18n.form.validations.debtorNumber"
            @keydown.space.prevent
          />
          <FormulateInput
            type="group"
            name="invoices"
            :repeatable="true"
            :help="i18n.form.help.invoices"
            :add-label="i18n.form.addlabel.invoice"
            validation="bail|min:1,length|max:3,length|unique:invoiceNumber"
            :validation-messages="i18n.form.validations.invoices"
          >
            <div class="row w-full">
              <!-- workaround for losing focus on first input -->
              <FormulateInput
                type="hidden"
                value="none"
              />
              <!-- /workaround -->
              <FormulateInput
                type="text"
                name="invoiceNumber"
                :label="i18n.form.labels.invoiceNumber"
                validation="bail|required:trim|number|min:6,length|max:10,length"
                :validation-messages="invoiceValidation"
              />
            </div>
          </FormulateInput>
        </div>
      </fieldset>

      <fieldset
        v-if="isChecked === 'no' && inDateRange"
        class="grid grid-cols-12 gap-6"
      >
        <div class="col-span-12">
          <slot scope="childcare-explanation" />
        </div>
      </fieldset>
    </wizard-step>
  </wizard>
</template>
<script>
  import KfRichText from '@/components/base/rich-text/RichText.vue';
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import axios from 'axios';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';

  export default {
    name: 'WizardChildcareAllowance',
    components: { Wizard, WizardStep, WizardStepIntro, KfRichText },
    props: {
      i18n: {
        type: Object,
        default: () => { },
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        isChecked: null,
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
        },
        request: {
          isSending: false,
          errorSending: false,
        },
        dateRange: {
          // in calendar day of month
          min: 5,
          max: 19,
        },
      };
    },
    computed: {
      invoiceValidation() {
        const { max, required, number } = this.i18n.form.validations.invoiceNumber;
        return {
          min: max,
          max,
          number,
          required,
        };
      },
      inDateRange() {
        const { min, max } = this.dateRange;
        const date = new Date();
        const day = date.getDate();
        return day > min && day < max;
      },
      personalDetails() {
        let adjustedPersonalData = { ...this.formData.personalDetails };
        delete adjustedPersonalData.invoices;
        return adjustedPersonalData;
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.personalDetails,
          paymentDeferral: {
            invoices: Object.values(this.formData.personalDetails.invoices),
          },
        };
      },
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;

        const requestVerificationToken = document.body.querySelector(
          'input[type=hidden][name=__RequestVerificationToken]',
        )?.value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          },
        };

        axios
          .post(`/umbraco/api/wizard/childcareallowance`, this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
